import React from "react";
import SharedLayout from "./SharedLayout";
import "../styles/shared.css";

const PrivacyView = ({ user, onLoginClick }) => {
  return (
    <SharedLayout user={user} onLoginClick={onLoginClick}>
      <div className="content-container">
        <h1>Privacy and Data Collection</h1>

        <section className="content-section">
          <h2>Collection of your information</h2>
          <p>
            Through your use of the{" "}
            <span className="sigmund-text">Sigmund</span> Platform, we may
            collect and use several types of information from and about you,
            including:
          </p>
          <ul>
            <li>
              <strong>Personal information</strong>, that we can reasonably use
              to directly or indirectly identify you, such as your name, birth
              date, gender, mailing address, e-mail address, telephone number,
              Internet protocol (IP) address, user name or any other identifier
              we may use to identify you ("Personal Information").
            </li>
            <li>
              <strong>Non-personal information</strong>, that does not directly
              reveal your identity or directly relate to an identifiable
              individual, such as demographic information, biometric or
              statistical or aggregated information. Biometric, statistical or
              aggregated data does not directly identify a specific person, but
              we may derive non-personal statistical or aggregated data from
              Personal Information. For example, we may aggregate Personal
              Information to calculate the percentage of users accessing a
              specific feature on the{" "}
              <span className="sigmund-text">Sigmund</span> Platform.
            </li>
            <li>
              <strong>Technical information</strong>, including your login
              information, browser type and version, time zone setting, browser
              plug-in types and versions, operating system and platform, or
              information about your internet connection, the equipment you use
              to access the <span className="sigmund-text">Sigmund</span>{" "}
              Platform, and usage details.
            </li>
            <li>
              <strong>
                Non-personal details about your{" "}
                <span className="sigmund-text">Sigmund</span> Platform
                interactions
              </strong>
              , including the full Uniform Resource Locators (URLs), clickstream
              to, through and from our Website and/or Mobile Application
              (including date and time), products you viewed or searched for,
              page response times, download errors, length of visits to certain
              pages, page interaction information (such as scrolling, clicks,
              and mouse-overs), or methods used to browse away from the page.
            </li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Anonymized information</h2>
          <p>
            We may use non-identifiable information created by us from your
            Personal Information to better understand and improve the{" "}
            <span className="sigmund-text">Sigmund</span> Platform and the
            services we deliver.
          </p>
        </section>

        <section className="content-section">
          <h2>Use of your information</h2>
          <p>
            We use the information that we collect about you or that you provide
            to us, including any Personal Information, as follows:
          </p>
          <ul>
            <li>
              To present our Website and/or Mobile Application and their
              contents to you.
            </li>
            <li>
              To provide you with information, products, or services that you
              request from us.
            </li>
            <li>
              To fulfill the purposes for which you provided the information or
              that were described when it was collected, or any other purpose
              for which you provide it.
            </li>
            <li>
              To provide you with notices about your account/subscription, if
              applicable, including expiration and renewal notices.
            </li>
            <li>
              To carry out our obligations and enforce our rights arising from
              any contracts with you, including for billing and collection or to
              comply with legal requirements.
            </li>
            <li>
              To notify you about changes to our Website, Mobile Application or
              any products or services we offer or provide.
            </li>
            <li>
              To improve our Website, Mobile Application, products and services,
              marketing and customer relationships and experiences.
            </li>
            <li>
              To allow you to participate in interactive features, social media
              or similar features on our Website and/or Mobile Application.
            </li>
            <li>
              To measure or understand the effectiveness of the advertising we
              serve to you and others, and to deliver relevant advertising to
              you.
            </li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Disclosure of your information</h2>
          <p>
            We may disclose aggregated information about our users, and
            information that does not identify any individual, without
            restriction or compensation to you. We may transfer or disclose any
            and/or information collected in accordance with the Privacy Policy
            as follows:
          </p>
          <ul>
            <li>
              To fulfill the purpose for which you provide Personal Information.
            </li>
            <li>
              To contractors, service providers, and other third parties we use
              to support our business (such as analytics and search engine
              providers that assist us with Website and/or Mobile Application
              improvement and optimization) and who are contractually obligated
              to keep Personal Information confidential, use it only for the
              purposes for which we disclose it to them, and to process the
              Personal Information with the same standards set out in this
              Privacy Policy.
            </li>
            <li>To our subsidiaries and affiliates.</li>
            <li>
              In accordance with applicable law, to a buyer or other successor
              in the event of a merger, divestiture, restructuring,
              reorganization, dissolution, or other sale or transfer of some or
              all of <span className="sigmund-text">Sigmund</span>'s assets,
              whether as a going concern or as part of bankruptcy, liquidation,
              or similar proceeding, in which Personal Information held by{" "}
              <span className="sigmund-text">Sigmund</span> about our customers
              and users is among the assets transferred.
            </li>
            <li>
              To advertisers and advertising networks that require the
              information to select and serve relevant advertisements to you and
              others. We do not disclose data about identifiable individuals to
              our advertisers, but we may provide them with aggregate
              information about our users (for example, we may inform them that
              400 women between 30 and 45 have clicked on their advertisement on
              a specific day). We may also use such aggregate information to
              help advertisers target a specific audience (for example, men in a
              specific location). We may make use of the Personal Information we
              have collected from you to enable us to display our advertisers'
              advertisement to that target audience.
            </li>
            <li>
              For any other purpose disclosed by us when you provide the
              information.
            </li>
            <li>With your consent.</li>
            <li>
              To comply with any court order, law or legal process, including to
              respond to any government or regulatory request, in accordance
              with applicable law.
            </li>
          </ul>
        </section>

        <section className="content-section">
          <h2>How we collect your information</h2>
          <p>
            We may use various methods to collect your information, including
            through:
          </p>
          <ul>
            <li>
              <strong>Direct interactions</strong> with you when you provide
              information to us, including:
              <ul>
                <li>
                  Information that you provided at the time of registering to
                  use our Mobile Application, subscribing to services delivered
                  through our Website and/or Mobile Application, posting
                  material, requesting further services, and any information
                  that you provide when filling in forms on the{" "}
                  <span className="sigmund-text">Sigmund</span> Platform.
                </li>
                <li>
                  Records and copies of your correspondence (including email
                  addresses), if you contact us, including in instances when you
                  contact us with a comment, question or complaint.
                </li>
                <li>
                  Your responses to questions that we might ask you to complete
                  for research purposes.
                </li>
                <li>
                  Details of transactions you carry out through the{" "}
                  <span className="sigmund-text">Sigmund</span> Platform and of
                  the fulfillment of your orders.
                </li>
                <li>
                  Your search queries on the{" "}
                  <span className="sigmund-text">Sigmund</span> Platform
                </li>
                <li>
                  User contributions, if you provide information for us to
                  publish or display on public areas of the Website or Mobile
                  Application or transmit to other{" "}
                  <span className="sigmund-text">Sigmund</span> Platform users
                  or third parties (collectively, "User Contributions").
                </li>
              </ul>
            </li>
            <li>
              <strong>Automated technologies or interactions</strong>, as you
              navigate through our Website. Information collected automatically
              may include usage details, IP addresses, and information collected
              through cookies, web beacons and/or other tracking technologies.
            </li>
            <li>
              <strong>Third parties or publicly available sources</strong>, for
              example, our business partners.
            </li>
          </ul>
          <p className="important-note">
            Please note that the <span className="sigmund-text">Sigmund</span>{" "}
            Platform is not intended for individuals under the age of majority.
            No one under the age of majority may provide any Personal
            Information to or on the{" "}
            <span className="sigmund-text">Sigmund</span> Platform. We do not
            knowingly collect Personal Information from individuals under the
            age of majority. If you are under the age of majority, do not use or
            provide any information on the{" "}
            <span className="sigmund-text">Sigmund</span> Platform, use any of
            the interactive or public features of the{" "}
            <span className="sigmund-text">Sigmund</span> Platform, or provide
            any information about yourself to us. If we learn we have collected
            or received Personal Information from an individual under the age of
            majority without verification of parental consent, we will delete
            that information. If you believe we might have any information from
            or about an individual under the age of majority, please contact as
            set out in the "Contact Us" section below.
          </p>
        </section>

        <section className="content-section">
          <h2>Information about our platform</h2>
          <p>
            As you use the <span className="sigmund-text">Sigmund</span>{" "}
            Platform, certain Personal Information may be passively collected by
            cookies, navigational data like Uniform Resource Locators (URLs) and
            third party tracking services, including:
          </p>
          <ul>
            <li>
              <strong>Access Device and Browser Information:</strong> When you
              access the <span className="sigmund-text">Sigmund</span> Platform
              from a computer or other device, we may collect information from
              that device, such as your Internet Protocol address (IP address),
              browser type, connection speed and access times (collectively,
              "Usage Information"). We use Usage Information to improve our
              products and services and to protect your data from unauthorized
              access.
            </li>
            <li>
              <strong>Cookies:</strong> The Website and/or Mobile Application
              may use a technology called "Cookies". A Cookie is a tiny element
              of data that the Website and/or Mobile Application may send to
              your browser, which may then be stored on your hard drive so that
              we can recognize your browser when you return. We use Cookies to
              remember your preferences and to authenticate you. You may set
              your browser to notify you when you receive a Cookie or to not
              accept certain Cookies. However, if you decide not to accept
              Cookies from the <span className="sigmund-text">Sigmund</span>{" "}
              Platform, you may not be able to take advantage of all of the
              features of the <span className="sigmund-text">Sigmund</span>{" "}
              Platform.
            </li>
            <li>
              <strong>Device and Usage Information:</strong> We may also collect
              device-related information from your mobile device or computer.
              This information is used to help us authenticate you, deliver
              content appropriate for your device's capabilities, and to deliver
              push notifications to notify about activity on your account.
              Examples of information that may be collected and used include
              your device's unique identifier, manufacturer, model, and
              operating system version. In addition, in the event our Mobile
              Application crash(es) on your mobile device we may receive
              information about your mobile device model software version and
              device carrier, which allows us to identify and fix bugs and
              otherwise improve the performance of our Mobile Application.
            </li>
            <li>
              <strong>Third Party Links:</strong> The Website and/or Mobile
              Application may contain links to other websites or platforms that{" "}
              <span className="sigmund-text">Sigmund</span> does not own or
              operate. Also, links to the Website and/or Mobile Application may
              be featured on third party websites or platforms on which we
              advertise. Except as provided in this Privacy Policy, we will not
              provide Information to these third parties without consent. We
              provide links to third party websites or platforms as a
              convenience to our users. These links are not intended as an
              endorsement of, or referral to, the linked websites or platforms.
              The linked websites or platforms have separate and independent
              privacy statements, notices and terms of use. We do not have any
              control over such websites or platforms, and therefore we have no
              responsibility or liability because the organizations that operate
              such linked websites or platforms may collect, use or disclose,
              secure and otherwise treat Personal Information.
            </li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Security of information</h2>
          <p>
            We understand that data security is a critical issue for our clients
            and we are committed to safeguarding the Personal Information in our
            custody and under our control. We have implemented reasonable
            administrative, technical and physical safeguards in an effort to
            protect against unauthorized access, use, loss, modification and
            disclosure of Personal Information in our custody and control.
          </p>
          <p>
            Our privacy practices are intended to comply with applicable
            provincial and federal privacy laws, including without limitation,
            the Personal Information Protection and Electronic Documents Act
            (Canada) ("PIPEDA"). We will maintain the privacy and security of
            your Personal Information as required by applicable privacy laws,
            including without limitation, the PIPEDA and any regulations under
            that Act. Personal Information may be stored outside of Canada.
          </p>
          <p>
            Unfortunately, the transmission of information via the Internet is
            not completely secure. Although we do our best to protect your
            Personal Information, we cannot guarantee the security of your
            Personal Information transmitted to our Website and/or Mobile
            Application. Any transmission of Personal Information is at your own
            risk. We are not responsible for circumvention of any privacy
            settings or security measures contained on the Website and/or Mobile
            Application.
          </p>
        </section>

        <section className="content-section">
          <h2>Access to information</h2>
          <p>
            <span className="sigmund-text">Sigmund</span> takes reasonable steps
            to ensure your Personal Information is accurate, complete, and up to
            date. If you become aware that any Personal Information in our
            possession about you is not correct, please contact us. You are
            entitled to a copy of the Personal Information that we have in our
            possession or under our control. We will take reasonable steps to
            verify your identity before granting access or making corrections.
            In addition, your right to access or correct your Personal
            Information is subject to certain legal restrictions.
          </p>
        </section>

        <section className="content-section">
          <h2>Retaining your information</h2>
          <p>
            We may retain any and all Personal Information that we are required
            to retain under any applicable laws and regulations for the full
            duration of time required under those laws and regulations. We may
            also retain non-identifiable information and continue to use this
            information in accordance with this Policy.
          </p>
          <p>
            You may request that we delete the Personal Information that we
            maintain about you, but please note that we may be required to
            maintain certain Personal Information to meet our legal obligations
            (in which case we will comply with your deletion request only after
            we have fulfilled such obligations). When we delete Personal
            Information, it will be deleted from the active database, but may
            remain in our archives and we may also retain Usage Information.
            After we delete Personal Information, we may retain non-identifiable
            Personal Information and will continue to use non-identifiable
            information as permitted under this Privacy Policy.
          </p>
        </section>

        <section className="content-section">
          <h2>Report violations</h2>
          <p>
            You should report any privacy or security violations, including any
            suspected or actual unauthorized access, use or loss, of Personal
            Information, to us by sending an email to{" "}
            <a href="mailto:info@ndatalyze.com">info@ndatalyze.com</a>.
          </p>
        </section>

        <section className="content-section">
          <h2>Changes to this policy</h2>
          <p>
            It is our policy to post any changes we make to our Privacy Policy
            on this page. If we make material changes to how we treat our users'
            Personal Information, we will notify you through a notice on the
            Website. We include the date the Privacy Policy was last revised at
            the bottom of the page. You are responsible for ensuring we have an
            up-to-date, active and deliverable email address for you, and for
            periodically visiting our Website and this Privacy Policy to check
            for any changes.
          </p>
        </section>

        <section className="content-section">
          <h2>Consumer data policies, compliancy, and support</h2>
          <h3>GDPR Compliance</h3>
          <ul>
            <li>
              Lawful, fair and transparent data processing (we will utilize data
              only for uses described clearly in our Privacy Policy).
            </li>
            <li>
              Limitation of purpose, data, and storage (we will not request any
              data of yours that is not useful for your time spent in the{" "}
              <span className="sigmund-text">Sigmund</span> application).
            </li>
            <li>
              Data subject rights (you can ask us what data we use and how we
              use it)
            </li>
          </ul>

          <h3>CCPA Compliance</h3>
          <ul>
            <li>
              You may request at any time what personal information{" "}
              <span className="sigmund-text">Sigmund</span> has collected.
            </li>
            <li>
              You may request <span className="sigmund-text">Sigmund</span> to
              delete your data.
            </li>
            <li>
              You may opt out of your personal information being sold or shared.
            </li>
          </ul>
          <p>
            All of this may be done by emailing{" "}
            <a href="mailto:info@ndatalyze.com">info@ndatalyze.com</a>. Please
            include your written request in the body paragraph of the email and
            your Member ID used to create your{" "}
            <span className="sigmund-text">Sigmund</span> account. We will then
            reach out to you with verification steps and to confirm your
            request.
          </p>
          <p>
            In addition, you will not be discriminated against by offering you
            different pricing or services, or by providing you with a different
            level of quality of service, because you exercise any of your
            privacy rights as a California resident.
          </p>
        </section>

        <section className="content-section">
          <h2>Deleting your account</h2>
          <p>
            If you want to permanently delete your{" "}
            <span className="sigmund-text">Sigmund</span> account, let us know
            at <a href="mailto:info@ndatalyze.com">info@ndatalyze.com</a>. Once
            the deletion process begins, you won't be able to reactivate your
            account or retrieve any of the content or information you have
            added.
          </p>
        </section>

        <section className="content-section">
          <h2>Contact us</h2>
          <p>
            We welcome your questions, comments and requests regarding this
            Privacy Policy and our privacy practices. Please contact us at{" "}
            <a href="mailto:info@ndatalyze.com">info@ndatalyze.com</a>.
          </p>
        </section>

        <div className="footer-text">
          <p>Effective Date: March, 2025</p>
        </div>
      </div>
    </SharedLayout>
  );
};

export default PrivacyView;
